import type { FC, ReactNode } from 'react'
import type { ArticleDetail, ContentCard } from '@/lib/content/types'

import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'
import { cmsKey } from '@/lib/portal/cms'
import { CtaBand } from '@/components/site/primitives'
import { ArticleHeader } from '@/components/site/article-header'
import { ArticleBody } from '@/components/site/article-body'
import { ArticleAuthor } from '@/components/site/article-author'
import { ArticleReport } from '@/components/site/article-report'
import { ArticleRelated } from '@/components/site/article-related'
import { AuthorAvatar, contentSection, field } from '@/components/site/article-meta'
import { useLocale } from '@/i18n/use-locale'
import { siteContent } from '@/i18n/site-content'

interface ArticleViewProps {
    item: ArticleDetail
    related: Array<ContentCard>
}

/** The full layout of an insight or blog post below the site header. */
export const ArticleView: FC<ArticleViewProps> = ({ item, related }): ReactNode => {
    const locale = useLocale()
    const copy = siteContent[locale].article
    const body = field(item, 'body', locale)
    const author = item.author
    const role = author ? field(author, 'role', locale) : null
    const section = contentSection(item.type)
    const backLabel = item.type === 'insight' ? copy.backToInsights : copy.backToBlog

    return (
        <>
            <article>
                <ArticleHeader item={item} />

                <div className='container-page grid gap-12 py-16 sm:py-20 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-20'>
                    <div className='min-w-0'>
                        {item.report_file && <ArticleReport href={item.report_file} className='mb-12 lg:hidden' />}

                        <ArticleBody html={body.value} />

                        {author && <ArticleAuthor author={author} className='mt-16 max-w-[68ch]' />}
                    </div>

                    <aside className='hidden lg:block'>
                        <div className='sticky top-28 flex flex-col gap-6'>
                            {item.report_file && <ArticleReport href={item.report_file} />}

                            {author && (
                                <div className='rounded-3xl border border-border bg-card p-6'>
                                    <p className='text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground'>
                                        {copy.writtenBy}
                                    </p>

                                    <div className='mt-4 flex items-center gap-3'>
                                        <AuthorAvatar author={author} />

                                        <div className='min-w-0 text-sm leading-snug'>
                                            <p className='font-semibold text-foreground' data-mdlfy-cms={cmsKey('authors', author.id, 'name')}>
                                                {author.name}
                                            </p>

                                            {role?.value && (
                                                <p className='mt-0.5 text-muted-foreground' data-mdlfy-cms={cmsKey('authors', author.id, role.column)}>
                                                    {role.value}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <Link href={section} className='inline-flex items-center gap-2 text-sm font-medium text-foreground/75 transition-colors hover:text-primary'>
                                <ArrowLeft className='size-4' aria-hidden='true' />
                                {backLabel}
                            </Link>
                        </div>
                    </aside>
                </div>
            </article>

            <ArticleRelated
                items={related}
                eyebrow={copy.relatedEyebrow}
                title={copy.relatedTitle}
                text={copy.relatedText}
                backHref={section}
                backLabel={backLabel}
            />

            <CtaBand />
        </>
    )
}
